import React, { useEffect, useState, FC, memo } from 'react';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import ru from 'date-fns/locale/ru';

import DateUtils from 'toolkit/DateUtils';
import useKeyDownCallback from 'toolkit/useKeyDownCallback';
import { KeyboardEventCode } from 'toolkit/types';
import { mobileQueryList, useMediaQueryChangeListener } from 'toolkit/useMedia';
import useScrollOutsideCallback from 'toolkit/useScrollOutsideCallback';

import IconChevronDown from 'ui/icons/ChevronDown';
import IconXCircle from 'ui/icons/XCircle';
import Button, { ButtonType } from 'ui/Button';

import { renderDateInputHeader } from './DatePickerHeader';
import { RenderHeaderMode } from './DatePickerHeader/types';
import DatePickerCalendarContainer from './DatePickerCalendarContainer';
import { DatePickerCalendarContainerProps } from './DatePickerCalendarContainer/types';
import { getRenderDayContents } from './DayRenderer';
import CustomInput from './CustomInput';

import { DateInputIconPosition, DateInputProps, DateInputStyle } from './types';
import { getMaxDate, getOpenToDate } from './utils';
import { DEFAULT_MIN_DATE, DEFAULT_MAX_DATE } from './consts';

import 'react-datepicker/dist/react-datepicker.css';

import styles from './index.module.scss';

export { DateInputStyle } from './types';

export { DEFAULT_MIN_DATE, DEFAULT_MAX_DATE };

const DateInput: FC<DateInputProps> = (props) => {
  const {
    elementId,
    value: propValue,
    minDate,
    maxDate,
    label,
    isDisabled,
    isLoading,
    showPreviousMonths = false,
    dateFormat = 'dd.MM.yyyy',
    placeholder = 'дд.мм.гггг',
    popperPlacement = 'right',
    style = DateInputStyle.INPUT,
    monthsShown: initialMonthsShown,
    calendarClassName,
    containerClassName,
    autoComplete,
    timezone,
    icon,
    iconPosition = DateInputIconPosition.RIGHT,
    isClearable = false,
    clearIcon,
    clearButtonClassName,
    onChange,
    onError,
    onEnterPressed,
  } = props;
  const isMobile = useMediaQueryChangeListener(mobileQueryList);
  const [ref, setRef] = useState<any>(null);

  const [currentDate, setCurrentDate] = useState<Date | null>(propValue);

  const currentMinDate = minDate || DEFAULT_MIN_DATE;
  const resolvedClearIcon = clearIcon || <IconXCircle />;
  const shouldRenderCustomClearButton = style === DateInputStyle.INPUT && isClearable && Boolean(resolvedClearIcon);

  const openToDate = getOpenToDate(currentMinDate, currentDate, isMobile, showPreviousMonths);

  const monthsShown =
    initialMonthsShown ||
    (isMobile ? Math.abs(DateUtils.differenceInCalendarMonths(currentDate || currentMinDate, currentMinDate)) + 24 : 1);

  const handleChange = (
    value: Date | null,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    setCurrentDate(value);
    onChange?.(value, event);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCurrentDate(null);
    onChange?.(null, event);
  };

  const onKeyDown = useKeyDownCallback<React.KeyboardEvent<HTMLElement>>(
    (event) => onEnterPressed?.(event as React.KeyboardEvent<HTMLInputElement>),
    {
      include: [KeyboardEventCode.ENTER],
    }
  );

  const onClose = () => {
    ref?.setOpen(false);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isMobile) {
      e.target.blur();
    }
  };

  useScrollOutsideCallback(ref, () => {
    if (!isMobile) {
      onClose();
    }
  });

  useEffect(() => {
    setCurrentDate(propValue);
  }, [propValue]);

  const CalendarContainer = ({ className, children }: DatePickerCalendarContainerProps) => (
    <DatePickerCalendarContainer
      className={className}
      htmlFor={elementId}
      scrollToId={`${elementId}_selected`}
      label={label}
      onClose={onClose}>
      {children}
    </DatePickerCalendarContainer>
  );

  return (
    <DatePicker
      id={elementId}
      title={label}
      locale={ru}
      selected={currentDate}
      autoComplete={autoComplete}
      key={`${elementId}_${currentDate?.getTime()}`}
      ref={(r) => setRef(r)}
      minDate={currentMinDate}
      wrapperClassName={classNames(styles.DatePicker, containerClassName)}
      calendarClassName={classNames(styles.DatePickerCalendar, calendarClassName)}
      dayClassName={() => styles.DatePickerCalendarDay}
      weekDayClassName={() => styles.DatePickerCalendarWeekDay}
      showPreviousMonths={isMobile && showPreviousMonths}
      customInput={
        style === DateInputStyle.INPUT ? (
          React.createElement(CustomInput, {
            id: `${elementId}_date-picker-wrapper`,
            clearButtonClassName,
            clearIcon: resolvedClearIcon,
            isClearable: shouldRenderCustomClearButton,
            onClear: handleClear,
          })
        ) : (
          <Button
            elementId="button_info_about_type"
            type={ButtonType.PLAIN}
            className={styles.DatePickerLinkButton}
            onClick={() => ref?.setOpen(true)}>
            {iconPosition === DateInputIconPosition.LEFT &&
              (icon || <IconChevronDown className={styles.DatePickerLinkButtonArrowIcon} />)}
            {label}
            {iconPosition === DateInputIconPosition.RIGHT &&
              (icon || <IconChevronDown className={styles.DatePickerLinkButtonArrowIcon} />)}
          </Button>
        )
      }
      renderDayContents={getRenderDayContents(elementId, currentDate, currentMinDate, showPreviousMonths, timezone)}
      renderCustomHeader={renderDateInputHeader(RenderHeaderMode.SINGLE, isMobile)}
      openToDate={openToDate}
      dateFormat={dateFormat}
      placeholderText={placeholder}
      withPortal={isMobile}
      maxDate={getMaxDate(showPreviousMonths, timezone)}
      disabled={isDisabled || isLoading}
      popperPlacement={popperPlacement}
      strictParsing
      calendarContainer={isMobile ? CalendarContainer : undefined}
      monthsShown={monthsShown}
      onChange={handleChange}
      onInputError={onError}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      filterDate={(date) => (maxDate ? date <= maxDate : true)}
      isClearable={isClearable && !shouldRenderCustomClearButton}
    />
  );
};

export default memo(DateInput);
